/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component. Matches the same exemption on
   table-internals/use-row-keyboard-navigation.ts. */
import type { RowData } from '@tanstack/react-table';
import * as React from 'react';

import type { TreeRow } from './DimeTreeTable.types.js';

interface TreeRowKeyDownOptions<TData extends RowData> {
  rows: readonly TreeRow<TData>[];
  /** Forces the chevron/expand-collapse keys to be no-ops while true — expansion display is
   *  already fully overridden by the `true` sentinel (use-tree-table.ts) whenever a search filter
   *  is active, so a real toggle here would silently corrupt the underlying expansion state
   *  instead of doing nothing. */
  hasActiveFilter: boolean;
  onSelectRow?: (node: TData) => void;
  /** Fires on Enter/Space for a leaf row when there's no onSelectRow -- mirrors TreeTableRow.tsx's
   *  own click handling, so keyboard activation reaches the same navigation onRowClick drives. */
  onRowClick?: (node: TData) => void;
}

/** The treegrid-specific half of DimeTreeTable's keyboard handling: expand/collapse, first-child
 *  and parent navigation, and Enter/Space selection. ArrowUp/ArrowDown are identical to
 *  DimeNestedTable's flat list and stay owned by useRowKeyboardNavigation itself. */
export function useTreeRowKeyDown<TData extends RowData>({
  rows,
  hasActiveFilter,
  onSelectRow,
  onRowClick,
}: TreeRowKeyDownOptions<TData>) {
  return React.useCallback(
    (
      event: React.KeyboardEvent,
      row: TreeRow<TData>,
      { moveActiveRow }: { moveActiveRow: (rowId: string) => void },
    ) => {
      if (event.key === 'ArrowRight') {
        if (row.getCanExpand() && !row.getIsExpanded() && !hasActiveFilter) {
          event.preventDefault();
          row.toggleExpanded();
        } else if (row.getCanExpand() && row.getIsExpanded() && row.subRows[0]) {
          // Already expanded: WAI-ARIA treegrid moves focus to the first child instead of doing
          // nothing, unlike DimeNestedTable's flat ArrowRight (which only ever expands).
          // Navigation only — safe to leave ungated even while a filter forces every row open.
          event.preventDefault();
          moveActiveRow(row.subRows[0].id);
        }
      } else if (event.key === 'ArrowLeft') {
        if (row.getIsExpanded() && !hasActiveFilter) {
          event.preventDefault();
          row.toggleExpanded();
        } else {
          // Collapsed/leaf, or a filter is forcing every row open: move to the parent instead of
          // doing nothing — the other half of the same WAI-ARIA treegrid pattern, and (while
          // filtered) the same inert-toggle guarantee the chevron gets.
          const parentRow = row.getParentRow();
          if (parentRow) {
            event.preventDefault();
            moveActiveRow(parentRow.id);
          }
        }
      } else if (event.key === 'Enter' || event.key === ' ') {
        // We own this key regardless of which inner branch fires below — a leaf in a browse-only
        // tree (not expandable, no onSelectRow) would otherwise fall through with neither inner
        // branch calling preventDefault, letting Space scroll the page instead of being consumed
        // by the treegrid.
        event.preventDefault();
        if (row.getCanExpand() && !row.getIsExpanded() && !hasActiveFilter) {
          row.toggleExpanded();
        } else if (onSelectRow) {
          // Already-expanded rows fall through to selection: aria-selected is set on an
          // expandable row whenever onSelectRow is supplied (TreeTableRow.tsx), so a keyboard
          // user must be able to reach it via Enter/Space too, not just a mouse click — the
          // first activation still expands, matching ArrowRight's affordance.
          onSelectRow(row.original);
        } else if (onRowClick && !row.getCanExpand()) {
          // Matches TreeTableRow.tsx's own mouse handling: onRowClick only ever reaches a leaf --
          // an expandable row toggles instead. Without this guard, a filtered tree (which forces
          // every row open, so the toggleExpanded branch above never fires) would let Enter/Space
          // fall through and call onRowClick on a parent row, something a mouse click never does.
          onRowClick(row.original);
        }
      } else if (event.key === 'Home' && rows[0]) {
        event.preventDefault();
        moveActiveRow(rows[0].id);
      } else if (event.key === 'End' && rows.length > 0) {
        event.preventDefault();
        moveActiveRow(rows[rows.length - 1]!.id);
      }
    },
    [rows, hasActiveFilter, onSelectRow, onRowClick],
  );
}
